import axiosInstance from '../utils/axiosInstance';

/** Serialise params; arrays become repeated keys (diagnosis=a&diagnosis=b). */
export function toSearchParams(params = {}) {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) v.forEach((x) => x !== '' && x !== null && x !== undefined && sp.append(k, x));
    else if (typeof v === 'boolean') sp.append(k, v ? '1' : '0');
    else sp.append(k, v);
  });
  return sp;
}

/** Readable error message, preferring the API's `error` field. */
export function errorMessage(err, fallback = 'Something went wrong.') {
  if (!err) return fallback;
  const data = err.response?.data;
  if (data && typeof data === 'object' && !(data instanceof Blob)) {
    if (typeof data.error === 'string') return data.error;
    if (typeof data.detail === 'string') return data.detail;
    const first = Object.entries(data)[0];
    if (first) return `${first[0]}: ${Array.isArray(first[1]) ? first[1].join(' ') : first[1]}`;
  }
  if (typeof data === 'string' && data.length < 300 && !data.trim().startsWith('<')) return data;
  if (err.response?.status === 503) return 'This feature is not available right now.';
  if (err.response?.status === 401 || err.response?.status === 403) return 'You are not allowed to do this (session expired?).';
  if (err.response?.status === 404) return 'Not found.';
  if (err.response?.status >= 500) return `Server error (${err.response.status}).`;
  if (err.code === 'ERR_NETWORK') return 'Cannot reach the server.';
  return err.message || fallback;
}

const get = (url, params) => axiosInstance.get(url, { params: params ? toSearchParams(params) : undefined }).then((r) => r.data);
const post = (url, body) => axiosInstance.post(url, body).then((r) => r.data);
const patch = (url, body) => axiosInstance.patch(url, body).then((r) => r.data);
const del = (url) => axiosInstance.delete(url).then((r) => r.data);

/** Download a file through axios (so the auth header is attached). */
export async function downloadFile(url, params, fallbackName = 'export.csv') {
  let res;
  try {
    res = await axiosInstance.get(url, {
      params: params ? toSearchParams(params) : undefined,
      responseType: 'blob',
    });
  } catch (err) {
    // Blob error bodies: try to surface the JSON `error`
    const data = err.response?.data;
    if (data instanceof Blob) {
      try {
        const parsed = JSON.parse(await data.text());
        err.response.data = parsed;
      } catch {
        /* not JSON */
      }
    }
    throw err;
  }
  const dispo = res.headers?.['content-disposition'] || '';
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(dispo);
  const name = match ? decodeURIComponent(match[1]) : fallbackName;
  const href = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 1000);
}

export const api = {
  meta: () => get('api/meta/'),
  overview: () => get('api/overview/'),

  items: (params) => get('api/items/', params),
  exportItems: (params) => downloadFile('api/items/export/', params, 'toxy-items.csv'),
  aiSearch: (query) => post('api/search/ai/', { query }),
  visualSearch: (file, params = {}) => {
    const fd = new FormData();
    fd.append('image', file);
    toSearchParams(params).forEach((v, k) => fd.append(k, v));
    return axiosInstance.post('api/search/visual/', fd).then((r) => r.data);
  },
  item: (id) => get(`api/items/${id}/`),
  similar: (id, focus = 'balanced') => get(`api/items/${id}/similar/`, { focus }),
  priceHistory: (id, days = 365) => get(`api/items/${id}/price-history/`, { days }),
  stockHistory: (id, days = 365) => get(`api/items/${id}/stock-history/`, { days }),

  catalogSearch: (q) => get('api/catalog/search/', { q }),
  substitutes: (id, focus, band, qty, filters = {}) => get(`api/catalog/${id}/substitutes/`, { focus, band, qty, ...filters }),
  curate: (id, candidate_ids, note) => post(`api/catalog/${id}/curate/`, { candidate_ids, note }),
  substitutePhoto: (file, params = {}) => {
    const fd = new FormData();
    fd.append('image', file);
    toSearchParams(params).forEach((v, k) => fd.append(k, v));
    return axiosInstance.post('api/substitute/photo/', fd).then((r) => r.data);
  },
  substituteDescribe: (text, params = {}) => post('api/substitute/describe/', { text, ...params }),
  curateAny: (source, candidate_ids, note, file) => {
    const fd = new FormData();
    fd.append('source', JSON.stringify(source || {}));
    fd.append('candidate_ids', JSON.stringify(candidate_ids));
    fd.append('note', note || '');
    if (file) fd.append('image', file);
    return axiosInstance.post('api/substitute/curate/', fd).then((r) => r.data);
  },

  /** Live Odoo stock for ≤ 100 ids → {stock: {"<id>": {free, forecast, incoming}}} */
  liveStock: async (ids) => {
    const stock = {};
    for (let i = 0; i < ids.length; i += 100) {
      const res = await post('api/stock/live/', { odoo_ids: ids.slice(i, i + 100) });
      Object.assign(stock, res.stock || {});
    }
    return { stock };
  },

  shortlists: () => get('api/shortlists/'),
  createShortlist: (body) => post('api/shortlists/', body),
  shortlist: (id) => get(`api/shortlists/${id}/`),
  updateShortlist: (id, body) => patch(`api/shortlists/${id}/`, body),
  deleteShortlist: (id) => del(`api/shortlists/${id}/`),
  addToShortlist: (id, body) => post(`api/shortlists/${id}/items/`, body),
  updateShortlistItem: (id, itemId, body) => patch(`api/shortlists/${id}/items/${itemId}/`, body),
  removeShortlistItem: (id, itemId) => del(`api/shortlists/${id}/items/${itemId}/`),
  exportShortlist: (id, name) => downloadFile(`api/shortlists/${id}/export/`, null, `${(name || 'shortlist').replace(/[^\w-]+/g, '-')}.csv`),
};
